import React, { Component } from "react";
import axios from "axios";
import moment from "moment";
import createSocket from "../helpers/createSocket";
import { NotificationManager } from "./common/react-notifications";
import ComponentToPrintNow from "../containers/pages/ComponentToPrintNow";
import ComponentToPrintForKitchen from "../containers/pages/ComponentToPrintForKitchen";
import { END_POINT, END_POINT_SOCKET } from "../constants/defaultValues";
import { getEnabledOnlinePrintTypes } from "../helpers/OnlinePrintQueue";
import { buildSilentPrintHtml, getStoredPrintPaperWidthMm } from "../helpers/Utils";

const POLL_INTERVAL_MS = 30000;


class ElectronOnlinePrintAgent extends Component {
  state = {
    items: [],
    billDate: "",
    timeStart: "",
    pinPrint: "",
    customerName: "",
    customerPhone: "",
    customerAddress: "",
    deliveryTime: "",
    tableName: "",
  };

  terminalId = "";
  settings = null;
  draining = false;
  mounted = false;
  pollTimer = null;
  socket = null;
  customerRef = React.createRef();
  customerComponentRef = React.createRef();
  kitchenRef = React.createRef();

  componentDidMount() {
    if (!window.electronAPI) return;
    this.mounted = true;
    window.addEventListener("online", this.scheduleDrain);
    this.initialize();
  }

  componentWillUnmount() {
    this.mounted = false;
    window.removeEventListener("online", this.scheduleDrain);
    if (this.pollTimer) clearInterval(this.pollTimer);
    if (this.socket) {
      this.socket.off("connect", this.scheduleDrain);
      this.socket.off("online-print-job-created", this.scheduleDrain);
      this.socket.close();
    }
  }

  initialize = async () => {
    try {
      this.terminalId = await window.electronAPI.getTerminalId();
      await this.refreshSettings();
      this.connectSocket();
      this.pollTimer = setInterval(this.poll, POLL_INTERVAL_MS);
      this.scheduleDrain();
    } catch (error) {
      console.error("[OnlinePrintAgent] Initialization failed:", error);
    }
  };

  connectSocket = () => {
    this.socket = createSocket();
    const roomId = localStorage.getItem("b") || localStorage.getItem("user_id");
    if (roomId) this.socket.emit("join room", { room_id: roomId });
    this.socket.on("connect", () => {
      if (roomId) this.socket.emit("join room", { room_id: roomId });
      this.scheduleDrain();
    });
    this.socket.on("online-print-job-created", this.scheduleDrain);
  };

  poll = async () => {
    await this.refreshSettings();
    this.scheduleDrain();
  };

  refreshSettings = async () => {
    const token = localStorage.getItem("access_token");
    if (!token) {
      this.settings = null;
      return;
    }
    try {
      const response = await axios.get(`${END_POINT}/setting`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      this.settings = response.data?.data || null;
    } catch (error) {
      console.error("[OnlinePrintAgent] Could not load settings:", error);
    }
  };

  getDocumentTypes = () => {
    if (!this.settings || !this.terminalId) return [];
    const enabled = getEnabledOnlinePrintTypes({
      customer: this.settings.auto_print_online_customer === true,
      kitchen: this.settings.auto_print_online_kitchen === true,
    });
    return enabled.filter((type) => {
      const assigned =
        type === "customer"
          ? this.settings.online_print_customer_terminal_id ||
            this.settings.online_print_terminal_id
          : this.settings.online_print_kitchen_terminal_id ||
            this.settings.online_print_terminal_id;
      return assigned === this.terminalId;
    });
  };

  scheduleDrain = () => {
    Promise.resolve().then(this.drain).catch((error) => {
      console.error("[OnlinePrintAgent] Queue failed:", error);
    });
  };

  drain = async () => {
    if (!this.mounted || this.draining) return;
    const documentTypes = this.getDocumentTypes();
    const token = localStorage.getItem("access_token");
    if (!token || !documentTypes.length) return;

    this.draining = true;
    try {
      while (this.mounted) {
        const response = await axios.post(
          `${END_POINT}/online-print-jobs/claim`,
          {
            terminal_id: this.terminalId,
            terminal_kind: "electron",
            document_types: documentTypes,
          },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const claimed = response.data?.data;
        if (!claimed?.job) break;
        try {
          await this.processJob(claimed);
        } catch (error) {
          console.error(
            `[OnlinePrintAgent] Job ${claimed.job.id} failed:`,
            error,
          );
          await this.report(
            claimed.job.id,
            false,
            error?.message || "Không thể dựng hoặc in nội dung",
          ).catch((reportError) => {
            console.error(
              `[OnlinePrintAgent] Could not report job ${claimed.job.id}:`,
              reportError,
            );
          });
        }
      }
    } catch (error) {
      if (error.response?.status !== 401 && error.response?.status !== 403) {
        console.error("[OnlinePrintAgent] Claim failed:", error);
      }
    } finally {
      this.draining = false;
    }
  };

  setPayload = (orders, jobId) =>
    new Promise((resolve) => {
      const first = orders[0] || {};
      let billDate = moment(first.date, ["YYYY-MM-DD", "DD/MM/YYYY"], true);
      if (!billDate.isValid()) billDate = moment(first.date);
      this.setState(
        {
          items: orders,
          billDate: billDate.isValid()
            ? billDate.format("DD/MM/YYYY")
            : moment().format("DD/MM/YYYY"),
          timeStart: first.time || "",
          pinPrint: first.pin_print || "",
          customerName: first.customer_name || "",
          customerPhone: first.phone || "",
          customerAddress: first.customer_address || "",
          deliveryTime: first.delivery_time || "",
          tableName: first.user_identify || "",
          jobId,
        },
        () => setTimeout(resolve, 150),
      );
    });

  processJob = async ({ job, orders }) => {
    const completedBills = (() => {
      try {
        return JSON.parse(localStorage.getItem("localPrintCompletedBills") || "[]");
      } catch (e) {
        return [];
      }
    })();

    if (job.bill_id && completedBills.includes(job.bill_id)) {
      await this.report(job.id, true);
      return;
    }

    if (this.getCompletedJobs().includes(job.id)) {
      await this.report(job.id, true);
      this.removeCompletedJob(job.id);
      return;
    }

    await this.setPayload(orders, job.id);
    const isCustomer = job.document_type === "customer";
    const printerName =
      localStorage.getItem(
        isCustomer ? "onlineCustomerPrinter" : "onlineKitchenPrinter",
      ) ||
      localStorage.getItem("defaultPrinter") ||
      localStorage.getItem("default_printer") ||
      null;
    if (!printerName) {
      NotificationManager.error(
        isCustomer
          ? "Chưa chọn máy in hóa đơn khách"
          : "Chưa chọn máy in phiếu bếp/bar",
        "Tự in đơn online",
        5000,
      );
      await this.report(
        job.id,
        false,
        isCustomer
          ? "Chưa chọn máy in hóa đơn khách"
          : "Chưa chọn máy in phiếu bếp/bar",
      );
      return;
    }

    const node = isCustomer ? this.customerRef.current : this.kitchenRef.current;
    if (
      isCustomer &&
      this.customerComponentRef.current &&
      typeof this.customerComponentRef.current.prepareForPrint === "function"
    ) {
      await this.customerComponentRef.current.prepareForPrint();
    }
    const result = node
      ? await window.electronAPI.printSilent(
          buildSilentPrintHtml(node.outerHTML, {
            paperWidthMm: getStoredPrintPaperWidthMm(
              isCustomer ? "customer" : "kitchen",
            ),
            documentType: isCustomer ? "customer" : "kitchen",
          }),
          printerName,
        )
      : { success: false, error: "Không dựng được nội dung in" };
    const printed = result?.success === true;
    if (printed) this.addCompletedJob(job.id);
    await this.report(job.id, printed, result?.error || "");
    if (printed) {
      this.removeCompletedJob(job.id);
    } else {
      NotificationManager.error(
        result?.error || `Không thể in bằng máy ${printerName}`,
        "Tự in đơn online",
        5000,
      );
    }
  };

  report = (jobId, success, error = "") => {
    const token = localStorage.getItem("access_token");
    return axios.post(
      `${END_POINT}/online-print-jobs/${jobId}/result`,
      {
        terminal_id: this.terminalId,
        success,
        error,
      },
      { headers: { Authorization: `Bearer ${token}` } },
    );
  };

  getCompletedJobs = () => {
    try {
      const jobs = JSON.parse(
        localStorage.getItem("onlinePrintCompletedJobs") || "[]",
      );
      return Array.isArray(jobs) ? jobs : [];
    } catch (error) {
      return [];
    }
  };

  addCompletedJob = (jobId) => {
    const jobs = this.getCompletedJobs();
    localStorage.setItem(
      "onlinePrintCompletedJobs",
      JSON.stringify([jobId, ...jobs.filter((id) => id !== jobId)].slice(0, 200)),
    );
  };

  removeCompletedJob = (jobId) => {
    localStorage.setItem(
      "onlinePrintCompletedJobs",
      JSON.stringify(this.getCompletedJobs().filter((id) => id !== jobId)),
    );
  };

  render() {
    if (!window.electronAPI) return null;
    const {
      items,
      billDate,
      timeStart,
      pinPrint,
      customerName,
      customerPhone,
      customerAddress,
      deliveryTime,
      tableName,
    } = this.state;
    return (
      <div style={{ display: "none" }} aria-hidden="true">
        <div ref={this.customerRef}>
          <ComponentToPrintNow
            ref={this.customerComponentRef}
            items={items}
            companyName={localStorage.getItem("companyName")}
            companyAddress={localStorage.getItem("companyAddress")}
            companyLogo={localStorage.getItem("companyLogo")}
            companyWebsite={localStorage.getItem("companyWebsite")}
            billDate={billDate}
            pin_print={pinPrint}
            discount={null}
            tax_fee={null}
            fee_other={null}
            timeStart={timeStart}
            customer_name={customerName}
            customer_phone={customerPhone}
            customer_address={customerAddress}
            delivery_time={deliveryTime}
            table_name={tableName}
            print_customer={this.settings?.print_customer_invoice !== false}
            show_order_context
            show_variant_badges
          />
        </div>
        <div ref={this.kitchenRef}>
          <ComponentToPrintForKitchen
            items={items}
            billDate={billDate}
            pin_print={pinPrint}
            show_variant_badges
          />
        </div>
      </div>
    );
  }
}

export default ElectronOnlinePrintAgent;
